import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import axios from 'axios'
import { createApiCall } from 'services'

/**
 * Mirrors the backend CertificateActivationTriggerEnum (@repo/types). Kept as a
 * local string-literal union because @repo/types is consumed type-only in the
 * platform (esbuild strips it) and isn't a runtime-resolvable dependency here.
 * The API DTO validates these values server-side.
 */
export type CertificateActivationTrigger =
  | 'ON_CREATION'
  | 'ON_PRIMARY_TRANSFER'
  | 'AT_LOCATION'

export type CertificateTriggerSettings = {
  id: string
  company_id: string
  trigger: string
  activation_trigger: CertificateActivationTrigger | null
  collect_email: boolean
  collect_location: boolean
  hide_powered_by: boolean
  webhook_url: string | null
  webhook_secret: string | null
}

const queryKey = (companyId: string) => ['certificate-trigger-settings', companyId]

/**
 * Org-level certificate trigger settings for a company.
 * Returns null when no settings row exists yet (GET 404) so callers can fall
 * back to a default and let the save upsert one.
 */
export const useCertificateTriggerSettings = (
  companyId: string,
  options?: { enabled?: boolean }
) => {
  const isEnabled = options?.enabled !== false && !!companyId

  const query = useQuery({
    queryKey: queryKey(companyId),
    queryFn: async (): Promise<CertificateTriggerSettings | null> => {
      try {
        const response = await createApiCall({
          method: 'GET',
          url: `/certificate-trigger-settings/${companyId}`,
        })
        return response.data as CertificateTriggerSettings
      } catch (error) {
        // No settings configured yet — not an error for this surface.
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

type UpdateActivationTriggerParams = {
  companyId: string
  activationTrigger: CertificateActivationTrigger
}

/**
 * Patches only activation_trigger — the API upsert preserves the existing
 * creation trigger and webhook config (partial update).
 */
export const useUpdateCertificateActivationTrigger = (): UseMutationResult<
  CertificateTriggerSettings,
  Error,
  UpdateActivationTriggerParams
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId, activationTrigger }: UpdateActivationTriggerParams) => {
      const response = await createApiCall({
        method: 'PUT',
        url: `/certificate-trigger-settings/${companyId}`,
        data: { company_id: companyId, activation_trigger: activationTrigger },
      })
      return response.data as CertificateTriggerSettings
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.companyId) })
    },
  })
}

type UpdateDataCollectionParams = {
  companyId: string
  collect_email: boolean
  collect_location: boolean
}

/**
 * Patches the data-collection settings (collect_email / collect_location) for a
 * company. Hits the same upsert endpoint as the activation trigger — the API
 * preserves the rest of the settings row (partial update).
 *
 * NOTE: createApiCall RESOLVES on 4xx (it does not throw), so we check the
 * response status explicitly and throw on >= 400 to route into onError.
 */
export const useUpdateDataCollectionSettings = (): UseMutationResult<
  CertificateTriggerSettings,
  Error,
  UpdateDataCollectionParams
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId, collect_email, collect_location }: UpdateDataCollectionParams) => {
      const response = await createApiCall({
        method: 'PUT',
        url: `/certificate-trigger-settings/${companyId}`,
        data: { company_id: companyId, collect_email, collect_location },
      })
      if (response.status >= 400) {
        throw new Error(
          (response.data as { message?: string })?.message ??
            'Failed to update data collection settings'
        )
      }
      return response.data as CertificateTriggerSettings
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.companyId) })
    },
  })
}

type UpdateWhiteLabelSettingParams = {
  companyId: string
  hide_powered_by: boolean
}

/**
 * Patches the white-label "hide Powered by Vera" badge setting (TD-066) for a
 * company. Hits the same upsert endpoint as the other trigger-settings
 * mutations — the API preserves the rest of the settings row (partial
 * update). Both the Features page and the Studio white-label card call this,
 * so toggling in either place stays in sync via the shared query key.
 *
 * NOTE: createApiCall RESOLVES on 4xx (it does not throw), so we check the
 * response status explicitly and throw on >= 400 to route into onError.
 */
export const useUpdateWhiteLabelSetting = (): UseMutationResult<
  CertificateTriggerSettings,
  Error,
  UpdateWhiteLabelSettingParams
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ companyId, hide_powered_by }: UpdateWhiteLabelSettingParams) => {
      const response = await createApiCall({
        method: 'PUT',
        url: `/certificate-trigger-settings/${companyId}`,
        data: { company_id: companyId, hide_powered_by },
      })
      if (response.status >= 400) {
        throw new Error(
          (response.data as { message?: string })?.message ??
            'Failed to update white-label setting'
        )
      }
      return response.data as CertificateTriggerSettings
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKey(variables.companyId) })
    },
  })
}
